import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  CheckCircle2, XCircle, AlertTriangle, Clock, Loader2, Building2, TrendingUp,
  FileText, Download, Sliders,
} from 'lucide-react';
import { toast } from 'sonner';

const apiBase = () => (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const formatCHF = (n) =>
  new Intl.NumberFormat('de-CH', { maximumFractionDigits: 2 }).format(n || 0);

export const PreemptiveOffer = () => {
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [finance, setFinance] = useState({ minSubscriptionShares: 27 });
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(false);
  const [mode, setMode] = useState('full');      // 'full' | 'partial' | 'decline'
  const [partialShares, setPartialShares] = useState(0);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const load = async () => {
    try {
      const r = await fetch(`${apiBase()}/api/preemptive/offer/${token}`);
      if (!r.ok) {
        if (r.status === 404) { setOffer(null); return; }
        throw new Error('Angebot konnte nicht geladen werden');
      }
      const d = await r.json();
      setOffer(d.offer);
      // load public finance settings (min shares)
      try {
        const fr = await fetch(`${apiBase()}/api/site/finance`);
        const fd = await fr.json();
        if (fd?.success && fd.finance) setFinance(fd.finance);
      } catch {/* ignore */}
    } catch (e) {
      toast.error('Angebot konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const respond = async () => {
    if (responding) return;
    let action = 'accept';
    let acceptedShares = null;
    let confirmTxt = '';
    if (mode === 'decline') {
      action = 'decline';
      confirmTxt = 'Möchten Sie auf das Bezugsrecht VERZICHTEN? Die Aktien gehen in den freien Verkauf.';
    } else if (mode === 'full') {
      acceptedShares = offer.offerShares;
      confirmTxt = `Volles Bezugsrecht (${acceptedShares} Aktien) annehmen?`;
    } else {
      const n = parseInt(partialShares, 10);
      if (!n || n < 1) { toast.error('Bitte Anzahl Aktien eingeben'); return; }
      if (n > offer.offerShares) { toast.error('Maximal ' + offer.offerShares + ' Aktien'); return; }
      const minS = finance.minSubscriptionShares || 27;
      if (n < offer.offerShares && n < minS) {
        toast.error('Mindestens ' + minS + ' Aktien bei Teilzeichnung');
        return;
      }
      acceptedShares = n;
      confirmTxt = `Teilzeichnung über ${acceptedShares} Aktien (CHF ${formatCHF(acceptedShares * offer.pricePerShare)}) annehmen?`;
    }
    if (!window.confirm(confirmTxt)) return;
    setResponding(true);
    try {
      const r = await fetch(`${apiBase()}/api/preemptive/offer/${token}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, acceptedShares }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || 'Fehler bei der Übermittlung');
      toast.success(action === 'accept' ? 'Annahme erfasst – Zeichnungsschein per E-Mail unterwegs' : 'Verzicht erfasst');
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setResponding(false);
    }
  };

  const downloadPdf = () => {
    const url = `${apiBase()}/api/preemptive/offer/${token}/pdf`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white">
        <Loader2 className="h-8 w-8 animate-spin text-[#C4A55A]" />
      </div>
    );
  }
  if (!offer) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white p-6">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-amber-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Angebot nicht gefunden</h1>
          <p className="text-gray-400">Der Link ist ungültig oder das Angebot wurde widerrufen.</p>
          <Link to="/" className="inline-block mt-6 text-[#C4A55A] underline">Zur Startseite</Link>
        </div>
      </div>
    );
  }

  const status = offer.status;
  const isOpen = status === 'sent';
  const isAccepted = status === 'accepted' || status === 'accepted_partial';
  const deadlineDate = offer.deadline ? new Date(offer.deadline) : null;
  const nominalValue = finance.nominalValue || 100;
  const agio = Math.max(0, (offer.pricePerShare || 0) - nominalValue);
  const minS = finance.minSubscriptionShares || 27;

  const partialTotal = (parseInt(partialShares, 10) || 0) * (offer.pricePerShare || 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-[#0a0a0a] to-black text-white">
      <header className="border-b border-[#C4A55A]/20 bg-black/80 backdrop-blur-md">
        <div className="container mx-auto px-4 py-4 flex items-center gap-3">
          <Building2 className="h-6 w-6 text-[#C4A55A]" />
          <h1 className="font-semibold">Löwenherz Invest AG · Bezugsrecht</h1>
        </div>
      </header>

      <main className="container mx-auto px-4 py-12 max-w-2xl">
        <div className="bg-[#0f0f0f] border border-[#C4A55A]/30 rounded-2xl p-6 md:p-10" data-testid="preemptive-offer-card">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#C4A55A] mb-3">
            <TrendingUp className="h-3.5 w-3.5" /> Persönliches Bezugsrechts-Angebot
          </div>
          <h2 className="text-3xl font-bold mb-2">Sehr geehrte/r {offer.fullName}</h2>
          <p className="text-gray-300 mb-6">
            als bestehende/r Aktionär/in der Löwenherz Invest AG bieten wir Ihnen
            das gesetzliche Bezugsrecht (Art. 652b OR) zur <strong className="text-white">{offer.trancheName}</strong> an.
          </p>

          <div className="bg-black/40 border border-white/5 rounded-xl p-5 mb-6 space-y-3 text-sm">
            <Row label="Ihr Bezugsrecht" value={`${offer.offerShares.toLocaleString('de-CH')} Namenaktien`} highlight />
            <Row label="Nennwert pro Aktie" value={`CHF ${formatCHF(nominalValue)}`} />
            <Row label="Ausgabepreis pro Aktie" value={`CHF ${formatCHF(offer.pricePerShare)}`} />
            {agio > 0 && (
              <Row label="Agio pro Aktie" value={`CHF ${formatCHF(agio)} (Reserven)`} muted />
            )}
            <Row label="Gesamt bei voller Zeichnung" value={`CHF ${formatCHF(offer.totalCost)}`} bold />
            {deadlineDate && (
              <Row label="Annahmefrist bis"
                   value={deadlineDate.toLocaleString('de-CH', { dateStyle: 'long', timeStyle: 'short' })} />
            )}
          </div>

          {offer.customMessage && (
            <div className="bg-[#C4A55A]/10 border-l-4 border-[#C4A55A] rounded-r-md px-4 py-3 mb-6 text-sm text-gray-200" data-testid="offer-custom-msg">
              {offer.customMessage}
            </div>
          )}

          {/* Status / Actions */}
          {isOpen ? (
            <div className="space-y-4">
              {/* Mode selector */}
              <div className="grid grid-cols-3 gap-2" data-testid="offer-mode-selector">
                <ModeBtn active={mode === 'full'} onClick={() => setMode('full')} icon={CheckCircle2}
                         label="Volle Zeichnung" testId="mode-full" />
                <ModeBtn active={mode === 'partial'} onClick={() => setMode('partial')} icon={Sliders}
                         label="Teilzeichnung" testId="mode-partial" />
                <ModeBtn active={mode === 'decline'} onClick={() => setMode('decline')} icon={XCircle}
                         label="Verzichten" testId="mode-decline" />
              </div>

              {mode === 'partial' && (
                <div className="bg-black/40 border border-[#C4A55A]/30 rounded-xl p-4 space-y-3" data-testid="partial-shares-input">
                  <label className="block">
                    <span className="text-xs uppercase tracking-wider text-[#C4A55A]">
                      Anzahl gewünschter Aktien (mind. {minS}, max. {offer.offerShares})
                    </span>
                    <input
                      type="number"
                      min={minS}
                      max={offer.offerShares}
                      step="1"
                      value={partialShares || ''}
                      onChange={(e) => setPartialShares(e.target.value)}
                      placeholder={`z.B. ${Math.max(minS, Math.floor(offer.offerShares / 2))}`}
                      className="mt-1 w-full bg-black/80 border border-[#C4A55A]/40 rounded-lg px-3 py-2 text-white text-lg font-semibold focus:border-[#C4A55A] focus:outline-none"
                      data-testid="partial-shares-field"
                    />
                  </label>
                  <input
                    type="range"
                    min={minS}
                    max={offer.offerShares}
                    step="1"
                    value={partialShares || minS}
                    onChange={(e) => setPartialShares(e.target.value)}
                    className="w-full accent-[#C4A55A]"
                    data-testid="partial-shares-slider"
                  />
                  <div className="text-sm text-gray-300 tabular-nums">
                    Zu überweisender Betrag: <strong className="text-[#C4A55A]">CHF {formatCHF(partialTotal)}</strong>
                  </div>
                </div>
              )}

              <button
                onClick={respond}
                disabled={responding}
                data-testid="offer-submit"
                className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg font-semibold disabled:opacity-50 ${
                  mode === 'decline'
                    ? 'bg-[#1a1a1a] border border-white/15 hover:bg-white/5 text-gray-200'
                    : 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white'
                }`}
              >
                {responding ? <Loader2 className="h-5 w-5 animate-spin" /> : (
                  mode === 'decline' ? <XCircle className="h-5 w-5" /> : <CheckCircle2 className="h-5 w-5" />
                )}
                {mode === 'decline' ? 'Verzicht bestätigen' :
                 mode === 'partial' ? 'Teilzeichnung absenden' : 'Volles Bezugsrecht annehmen'}
              </button>
            </div>
          ) : isAccepted ? (
            <div className="space-y-4">
              <StatusBox icon={CheckCircle2} color="emerald"
                label={status === 'accepted_partial' ? 'Teilzeichnung angenommen' : 'Angebot angenommen'}
                detail={`Sie haben ${(offer.acceptedShares || offer.offerShares).toLocaleString('de-CH')} Aktien gezeichnet (CHF ${formatCHF(offer.acceptedAmount || offer.totalCost)}). Ihr persönlicher Zeichnungsschein wurde Ihnen per E-Mail zugestellt.`}
              />
              <button
                onClick={downloadPdf}
                data-testid="offer-pdf-download"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#C4A55A] hover:bg-[#D4B56A] text-black font-semibold"
              >
                <Download className="h-5 w-5" />
                Zeichnungsschein als PDF herunterladen
              </button>
            </div>
          ) : status === 'declined' ? (
            <StatusBox icon={XCircle} color="gray" label="Verzicht erfasst"
              detail="Vielen Dank für Ihre Rückmeldung. Ihr Anteil bleibt unverändert; die Aktien gehen in den freien Verkauf." />
          ) : status === 'expired' ? (
            <StatusBox icon={Clock} color="amber" label="Annahmefrist abgelaufen"
              detail="Die Frist für die Annahme dieses Bezugsrechts ist vorüber. Die Aktien wurden für den freien Markt freigegeben." />
          ) : (
            <div className="text-center text-gray-500 py-6">Status: {status}</div>
          )}

          <div className="mt-8 pt-6 border-t border-white/5 text-xs text-gray-500 flex items-center gap-2">
            <FileText className="h-3.5 w-3.5" />
            Fragen? <a href="mailto:office@loewenherz.ag" className="text-[#C4A55A] underline">office@loewenherz.ag</a>
          </div>
        </div>
      </main>
    </div>
  );
};

const ModeBtn = ({ active, onClick, icon: Icon, label, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`flex flex-col items-center gap-1 px-3 py-3 rounded-lg border text-xs font-medium transition-all ${
      active
        ? 'bg-[#C4A55A]/15 border-[#C4A55A] text-[#C4A55A]'
        : 'bg-black/40 border-white/10 text-gray-400 hover:border-white/30'
    }`}
  >
    <Icon className="h-5 w-5" />
    {label}
  </button>
);

const Row = ({ label, value, highlight, bold, muted }) => (
  <div className="flex justify-between items-center">
    <span className={muted ? 'text-gray-500' : 'text-gray-400'}>{label}</span>
    <span className={`tabular-nums ${
      highlight ? 'text-[#C4A55A] font-bold text-lg' :
      bold ? 'text-white font-bold' :
      muted ? 'text-gray-400 text-xs' :
      'text-white'
    }`}>
      {value}
    </span>
  </div>
);

const StatusBox = ({ icon: Icon, color, label, detail }) => {
  const colorClasses = {
    emerald: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300',
    gray:    'bg-white/5 border-white/15 text-gray-300',
    amber:   'bg-amber-500/15 border-amber-500/40 text-amber-300',
  }[color] || 'bg-white/5 text-gray-300';
  return (
    <div className={`rounded-xl border p-4 flex gap-3 ${colorClasses}`} data-testid={`offer-status-${color}`}>
      <Icon className="h-6 w-6 flex-shrink-0 mt-0.5" />
      <div>
        <div className="font-semibold mb-1">{label}</div>
        <div className="text-sm opacity-80">{detail}</div>
      </div>
    </div>
  );
};
